import React, { useState } from 'react';
import { PlusIcon, SearchIcon, CalendarIcon, ListIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, RefreshCwIcon, CheckIcon, DownloadIcon, FileSpreadsheetIcon, ClockIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Appointment, AppointmentStatus } from '../../types';
import { formatTime, getToday, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getDayOfWeekKey, getDayOfWeekName, getDoctorPrefix } from '../../utils/helpers';
import { validateAppointmentForm } from '../../utils/validation';
import { exportAppointmentsToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { emailNotificationService } from '../../utils/emailNotifications';
import { getRolePermissions } from '../../types';
import { FormSection } from '../ui/FormSection';
export function Appointments() {
  const {
    appointments,
    addAppointment,
    cancelAppointment,
    rescheduleAppointment,
    completeAppointment,
    patients,
    doctors,
    services,
    specialties,
    getPatient,
    getDoctor,
    getService,
    getSpecialty,
    getDoctorsBySpecialty,
    checkDoctorAvailability,
    getAppointmentsByDate
  } = useData();
  const {
    user
  } = useAuth();
  const {
    addNotification
  } = useNotifications();
  const isAdmin = user?.role === 'administrador';
  // Determine if user has "own" permission (propias) for appointments
  const userPermissions = user ? getRolePermissions(user.role) : null;
  const appointmentPermissionLevel = userPermissions?.appointments || 'none';
  const isOwnOnly = appointmentPermissionLevel === 'own';
  // Find the doctor record linked to the current user (match by email)
  const linkedDoctor = isOwnOnly && user ? doctors.find(d => d.email.toLowerCase().trim() === user.email.toLowerCase().trim()) : null;
  // If "own" permission but no linked doctor found, show warning
  const hasOwnButNoDoctor = isOwnOnly && !linkedDoctor;
  const [activeView, setActiveView] = useState<'list' | 'calendar'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  // Form state
  const [formData, setFormData] = useState({
    patientId: '',
    doctorId: '',
    serviceId: '',
    date: getToday(),
    time: '09:00',
    notes: ''
  });
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Cancel form state
  const [cancelReason, setCancelReason] = useState('');
  // Reschedule form state
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    time: ''
  });
  // Complete form state
  const [completeData, setCompleteData] = useState({
    nextAppointmentDate: '',
    nextAppointmentNotes: ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const today = getToday();
  const filteredAppointments = appointments.filter(apt => {
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const matchesSearch = !searchTerm || patient?.name.toLowerCase().includes(searchTerm.toLowerCase()) || doctor?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || apt.status === filterStatus;
    // CRITICAL: If "own" permission, only show appointments for this doctor
    const matchesOwnership = isOwnOnly && linkedDoctor ? apt.doctorId === linkedDoctor.id : true;
    return matchesSearch && matchesStatus && matchesOwnership;
  });
  const {
    paginatedItems: paginatedAppointments,
    currentPage: aptPage,
    totalPages: aptTotalPages,
    totalItems: aptTotalItems,
    startIndex: aptStartIndex,
    endIndex: aptEndIndex,
    goToPage: aptGoToPage,
    nextPage: aptNextPage,
    prevPage: aptPrevPage
  } = usePagination(filteredAppointments);
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'programada',
    label: 'Programada'
  }, {
    value: 'completada',
    label: 'Completada'
  }, {
    value: 'cancelada',
    label: 'Cancelada'
  }, {
    value: 'reprogramada',
    label: 'Reprogramada'
  }];
  const patientOptions = patients.map(p => ({
    value: p.id,
    label: p.name
  }));
  const specialtyOptions = [{
    value: '',
    label: 'Todas las especialidades'
  }, ...specialties.map(s => ({
    value: s.id,
    label: s.name
  }))];
  const doctorOptions = selectedSpecialty ? getDoctorsBySpecialty(selectedSpecialty).map(d => ({
    value: d.id,
    label: `Dr. ${d.name}`
  })) : doctors.map(d => ({
    value: d.id,
    label: `Dr. ${d.name}`
  }));
  const serviceOptions = services.map(s => ({
    value: s.id,
    label: `${s.name} (${s.duration} min)`
  }));
  // Calendar
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }
  const getDateString = (day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => {
      const matchesDate = a.date === dateStr && a.status !== 'cancelada';
      // If "own" permission, only count this doctor's appointments
      const matchesOwnership = isOwnOnly && linkedDoctor ? a.doctorId === linkedDoctor.id : true;
      return matchesDate && matchesOwnership;
    }).length;
  };
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const selectedDateAppointments = getAppointmentsByDate(selectedDate);
  const openNewForm = () => {
    setSelectedAppointment(null);
    setFormData({
      patientId: '',
      // Auto-select the linked doctor if "own" permission
      doctorId: isOwnOnly && linkedDoctor ? linkedDoctor.id : '',
      serviceId: '',
      date: getToday(),
      time: '09:00',
      notes: ''
    });
    setSelectedSpecialty('');
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openCancelModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setCancelReason('');
    setIsCancelOpen(true);
  };
  const openRescheduleModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({
      date: appointment.date,
      time: appointment.time
    });
    setIsRescheduleOpen(true);
  };
  const openCompleteModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setCompleteData({
      nextAppointmentDate: '',
      nextAppointmentNotes: ''
    });
    setIsCompleteOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateAppointmentForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    // Check doctor schedule first for better error messages
    const selectedDoctor = getDoctor(formData.doctorId);
    if (selectedDoctor && selectedDoctor.schedule) {
      const dayKey = getDayOfWeekKey(formData.date) as keyof typeof selectedDoctor.schedule;
      const daySchedule = selectedDoctor.schedule[dayKey];
      const dayName = getDayOfWeekName(formData.date);
      const prefix = getDoctorPrefix(selectedDoctor.gender);
      if (!daySchedule) {
        setFormErrors({
          date: `${prefix} ${selectedDoctor.name} no trabaja los ${dayName}`
        });
        return;
      }
      const requestedMinutes = parseInt(formData.time.split(':')[0]) * 60 + parseInt(formData.time.split(':')[1]);
      const startMinutes = parseInt(daySchedule.start.split(':')[0]) * 60 + parseInt(daySchedule.start.split(':')[1]);
      const endMinutes = parseInt(daySchedule.end.split(':')[0]) * 60 + parseInt(daySchedule.end.split(':')[1]);
      if (requestedMinutes < startMinutes || requestedMinutes >= endMinutes) {
        setFormErrors({
          time: `Fuera del horario. ${dayName}: ${formatTime(daySchedule.start)} - ${formatTime(daySchedule.end)}`
        });
        return;
      }
    }
    // Check availability (conflicting appointments)
    if (!checkDoctorAvailability(formData.doctorId, formData.date, formData.time)) {
      setFormErrors({
        time: 'El doctor ya tiene una cita en este horario'
      });
      return;
    }
    const service = getService(formData.serviceId);
    const patient = getPatient(formData.patientId);
    const doctor = getDoctor(formData.doctorId);
    addAppointment({
      ...formData,
      duration: service?.duration || 30,
      status: 'programada'
    });
    // Bell notification
    addNotification({
      type: 'appointment_created',
      title: 'Nueva Cita Creada',
      message: `Cita para ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} el ${formData.date} a las ${formatTime(formData.time)} — ${service?.name || 'Servicio'}`,
      relatedId: formData.patientId
    });
    // Email notification with user feedback
    if (patient && doctor && service) {
      emailNotificationService.sendAppointmentCreated({
        patientName: patient.name,
        patientEmail: patient.email,
        doctorName: doctor.name,
        serviceName: service.name,
        date: formData.date,
        time: formData.time,
        duration: service.duration,
        notes: formData.notes
      }).then(result => {
        if (result.success) {
          addNotification({
            type: 'appointment_created',
            title: '📧 Correo Enviado',
            message: `Notificación enviada a ${patient.email}`,
            relatedId: formData.patientId
          });
        } else if (emailNotificationService.isEnabled()) {
          addNotification({
            type: 'appointment_cancelled',
            title: '⚠️ Error de Correo',
            message: `No se pudo enviar correo a ${patient.email}: ${result.error || 'Error desconocido'}`,
            relatedId: formData.patientId
          });
        }
      }).catch(err => {
        console.error('Error enviando email de cita creada:', err);
        if (emailNotificationService.isEnabled()) {
          addNotification({
            type: 'appointment_cancelled',
            title: '⚠️ Error de Correo',
            message: `No se pudo enviar correo a ${patient.email}`,
            relatedId: formData.patientId
          });
        }
      });
    }
    setIsFormOpen(false);
  };
  const handleCancel = () => {
    if (selectedAppointment) {
      const patient = getPatient(selectedAppointment.patientId);
      const doctor = getDoctor(selectedAppointment.doctorId);
      const service = getService(selectedAppointment.serviceId);
      cancelAppointment(selectedAppointment.id, cancelReason);
      // Bell notification
      addNotification({
        type: 'appointment_cancelled',
        title: 'Cita Cancelada',
        message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} del ${selectedAppointment.date} fue cancelada. Motivo: ${cancelReason || 'No especificado'}`,
        relatedId: selectedAppointment.id
      });
      // Email notification with user feedback
      if (patient && doctor && service) {
        emailNotificationService.sendAppointmentCancelled({
          patientName: patient.name,
          patientEmail: patient.email,
          doctorName: doctor.name,
          serviceName: service.name,
          date: selectedAppointment.date,
          time: selectedAppointment.time,
          duration: selectedAppointment.duration,
          cancelReason: cancelReason
        }).then(result => {
          if (result.success) {
            addNotification({
              type: 'appointment_created',
              title: '📧 Correo Enviado',
              message: `Notificación de cancelación enviada a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          } else if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de cancelación a ${patient.email}: ${result.error || 'Error desconocido'}`,
              relatedId: selectedAppointment.id
            });
          }
        }).catch(err => {
          console.error('Error enviando email de cita cancelada:', err);
          if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de cancelación a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          }
        });
      }
      setIsCancelOpen(false);
    }
  };
  const handleReschedule = () => {
    if (selectedAppointment) {
      if (!checkDoctorAvailability(selectedAppointment.doctorId, rescheduleData.date, rescheduleData.time, selectedAppointment.id)) {
        alert('El doctor no está disponible en este horario');
        return;
      }
      const patient = getPatient(selectedAppointment.patientId);
      const doctor = getDoctor(selectedAppointment.doctorId);
      const service = getService(selectedAppointment.serviceId);
      rescheduleAppointment(selectedAppointment.id, rescheduleData.date, rescheduleData.time);
      // Bell notification
      addNotification({
        type: 'appointment_rescheduled',
        title: 'Cita Reprogramada',
        message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} fue reprogramada de ${selectedAppointment.date} ${formatTime(selectedAppointment.time)} a ${rescheduleData.date} ${formatTime(rescheduleData.time)}`,
        relatedId: selectedAppointment.id
      });
      // Email notification with user feedback
      if (patient && doctor && service) {
        emailNotificationService.sendAppointmentRescheduled({
          patientName: patient.name,
          patientEmail: patient.email,
          doctorName: doctor.name,
          serviceName: service.name,
          date: rescheduleData.date,
          time: rescheduleData.time,
          duration: selectedAppointment.duration,
          oldDate: selectedAppointment.date,
          oldTime: selectedAppointment.time
        }).then(result => {
          if (result.success) {
            addNotification({
              type: 'appointment_created',
              title: '📧 Correo Enviado',
              message: `Notificación de reprogramación enviada a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          } else if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de reprogramación a ${patient.email}: ${result.error || 'Error desconocido'}`,
              relatedId: selectedAppointment.id
            });
          }
        }).catch(err => {
          console.error('Error enviando email de cita reprogramada:', err);
          if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de reprogramación a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          }
        });
      }
      setIsRescheduleOpen(false);
    }
  };
  const handleComplete = async () => {
    if (selectedAppointment) {
      try {
        const patient = getPatient(selectedAppointment.patientId);
        const doctor = getDoctor(selectedAppointment.doctorId);
        const service = getService(selectedAppointment.serviceId);
        await completeAppointment(selectedAppointment.id, completeData.nextAppointmentDate, completeData.nextAppointmentNotes);
        // Bell notification
        const nextInfo = completeData.nextAppointmentDate ? ` — Próxima cita: ${completeData.nextAppointmentDate}` : '';
        addNotification({
          type: 'appointment_created',
          title: 'Cita Completada',
          message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} (${service?.name || 'Servicio'}) fue completada exitosamente.${nextInfo}`,
          relatedId: selectedAppointment.id
        });
        setIsCompleteOpen(false);
      } catch (error) {
        console.error('Error completando cita:', error);
        alert('Error al completar la cita. Intente nuevamente.');
      }
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportAppointmentsToExcel(appointments, (patientId: string) => getPatient(patientId)?.name || '-', (doctorId: string) => getDoctor(doctorId)?.name || '-', (serviceId: string) => getService(serviceId)?.name || '-');
    setIsExporting(false);
  };
  const viewTabs = [{
    id: 'list',
    label: 'Lista',
    icon: <ListIcon className="w-4 h-4" data-id="element-91" />
  }, {
    id: 'calendar',
    label: 'Calendario',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-92" />
  }];
  return <div className="space-y-6" data-id="element-93">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-94">
        <div data-id="element-95">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-96">
            Citas
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-97">
            {isOwnOnly && linkedDoctor ? `Citas de Dr. ${linkedDoctor.name}` : 'Gestión de citas médicas'}
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-99" />} onClick={openNewForm} data-id="element-98">
          Nueva Cita
        </Button>
      </div>

      {/* Warning if "own" permission but no linked doctor */}
      {hasOwnButNoDoctor && <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800" data-id="element-100">
          <div className="flex items-center gap-3 p-1" data-id="element-101">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0" data-id="element-102">
              <ClockIcon className="w-5 h-5 text-white" data-id="element-103" />
            </div>
            <div data-id="element-104">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-105">
                Doctor no vinculado
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-400" data-id="element-106">
                Tu cuenta ({user?.email}) no está vinculada a ningún registro de
                doctor. Contacta al administrador para que el email de tu
                usuario coincida con el email de tu registro de doctor.
              </p>
            </div>
          </div>
        </Card>}

      {/* Beautiful Export Card - Only for Admin */}
      {isAdmin && <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800" data-id="element-107">
          <div className="flex items-center justify-between" data-id="element-108">
            <div className="flex items-center gap-4" data-id="element-109">
              <div className="w-14 h-14 rounded-xl bg-green-500 dark:bg-green-600 flex items-center justify-center shadow-lg shadow-green-500/20" data-id="element-110">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-111" />
              </div>
              <div data-id="element-112">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-113">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-114">
                  Descarga todas las citas en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-115">
                  <div className="flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400" data-id="element-116">
                    <ClockIcon className="w-4 h-4" data-id="element-117" />
                    <span className="font-medium" data-id="element-118">
                      {appointments.length} citas
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-119">
                    • Incluye: Paciente, Doctor, Servicio, Fecha, Estado
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-121" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/30 transition-all duration-200" data-id="element-120">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* View Toggle and Filters */}
      <Card data-id="element-122">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4" data-id="element-123">
          <div className="flex gap-2" data-id="element-124">
            {viewTabs.map(tab => <button key={tab.id} onClick={() => setActiveView(tab.id as 'list' | 'calendar')} className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${activeView === tab.id ? 'bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'}
                `} data-id="element-125">
                {tab.icon}
                {tab.label}
              </button>)}
          </div>
          <div className="flex-1 flex gap-4" data-id="element-126">
            <div className="flex-1 relative" data-id="element-127">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-128" />
              <Input placeholder="Buscar por paciente o doctor..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-129" />
            </div>
            <div className="w-48" data-id="element-130">
              <Select options={statusOptions} value={filterStatus} onChange={setFilterStatus} placeholder="Estado" data-id="element-131" />
            </div>
          </div>
        </div>
      </Card>

      {/* List View */}
      {activeView === 'list' && <Card padding="none" data-id="element-132">
          <div className="overflow-x-auto" data-id="element-133">
            <table className="w-full" data-id="element-134">
              <thead data-id="element-135">
                <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-136">
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-137">
                    Paciente
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-138">
                    Doctor
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-139">
                    Servicio
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-140">
                    Fecha y Hora
                  </th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-141">
                    Estado
                  </th>
                  <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-142">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-143">
                {filteredAppointments.length === 0 ? <tr data-id="element-144">
                    <td colSpan={6} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-145">
                      No se encontraron citas
                    </td>
                  </tr> : paginatedAppointments.map(apt => {
              const patient = getPatient(apt.patientId);
              const doctor = getDoctor(apt.doctorId);
              const service = getService(apt.serviceId);
              const canModify = apt.status === 'programada' || apt.status === 'reprogramada';
              return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-146">
                        <td className="py-4 px-6" data-id="element-147">
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-148">
                            {patient?.name || '-'}
                          </p>
                        </td>
                        <td className="py-4 px-6" data-id="element-149">
                          <p className="text-gray-900 dark:text-gray-100" data-id="element-150">
                            Dr. {doctor?.name || '-'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-151">
                            {doctor && getSpecialty(doctor.specialtyId)?.name}
                          </p>
                        </td>
                        <td className="py-4 px-6" data-id="element-152">
                          <p className="text-gray-900 dark:text-gray-100" data-id="element-153">
                            {service?.name || '-'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-154">
                            {apt.duration} min
                          </p>
                        </td>
                        <td className="py-4 px-6" data-id="element-155">
                          <p className="text-gray-900 dark:text-gray-100" data-id="element-156">
                            {apt.date}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-157">
                            {formatTime(apt.time)}
                          </p>
                          {apt.status === 'completada' && apt.nextAppointmentDate && <p className="text-xs text-sky-600 dark:text-sky-400 mt-1 flex items-center gap-1" data-id="element-158">
                                <CalendarIcon className="w-3 h-3" data-id="element-159" />
                                Próxima: {apt.nextAppointmentDate}
                              </p>}
                        </td>
                        <td className="py-4 px-6" data-id="element-160">
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-161">
                            {getStatusName(apt.status)}
                          </Badge>
                        </td>
                        <td className="py-4 px-6" data-id="element-162">
                          <div className="flex items-center justify-end gap-1" data-id="element-163">
                            {canModify && <>
                                <button onClick={() => openCompleteModal(apt)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Completar" data-id="element-164">
                                  <CheckIcon className="w-4 h-4" data-id="element-165" />
                                </button>
                                <button onClick={() => openRescheduleModal(apt)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Reprogramar" data-id="element-166">
                                  <RefreshCwIcon className="w-4 h-4" data-id="element-167" />
                                </button>
                                <button onClick={() => openCancelModal(apt)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Cancelar" data-id="element-168">
                                  <XIcon className="w-4 h-4" data-id="element-169" />
                                </button>
                              </>}
                          </div>
                        </td>
                      </tr>;
            })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="px-6 pb-4" data-id="element-170">
            <Pagination currentPage={aptPage} totalPages={aptTotalPages} totalItems={aptTotalItems} startIndex={aptStartIndex} endIndex={aptEndIndex} onPageChange={aptGoToPage} onNext={aptNextPage} onPrev={aptPrevPage} data-id="element-171" />
          </div>
        </Card>}

      {/* Calendar View */}
      {activeView === 'calendar' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-172">
          {/* Calendar */}
          <Card className="lg:col-span-2" data-id="element-173">
            <CardHeader data-id="element-174">
              <div className="flex items-center justify-between" data-id="element-175">
                <CardTitle data-id="element-176">Calendario de Citas</CardTitle>
                <div className="flex items-center gap-2" data-id="element-177">
                  <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-178">
                    <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-179" />
                  </button>
                  <span className="text-lg font-medium text-gray-900 dark:text-white min-w-[160px] text-center" data-id="element-180">
                    {getMonthName(month)} {year}
                  </span>
                  <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-181">
                    <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-182" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-183">
              <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-184">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2" data-id="element-185">
                      {day}
                    </div>)}
              </div>
              <div className="grid grid-cols-7 gap-1" data-id="element-186">
                {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} className="aspect-square" data-id="element-187" />;
              }
              const dateStr = getDateString(day);
              const appointmentCount = getAppointmentCountForDay(day);
              const isToday = dateStr === today;
              const isSelected = dateStr === selectedDate;
              return <button key={day} onClick={() => setSelectedDate(dateStr)} className={`
                        aspect-square rounded-lg text-sm p-1
                        flex flex-col items-center justify-center
                        transition-colors
                        ${isToday ? 'bg-sky-500 text-white' : ''}
                        ${isSelected && !isToday ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 ring-2 ring-sky-500' : ''}
                        ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}
                      `} data-id="element-188">
                      <span className="font-medium" data-id="element-189">{day}</span>
                      {appointmentCount > 0 && <span className={`
                          text-[10px] px-1.5 py-0.5 rounded-full mt-0.5
                          ${isToday ? 'bg-sky-400 text-white' : 'bg-sky-100 text-sky-600 dark:bg-sky-900 dark:text-sky-400'}
                        `} data-id="element-190">
                          {appointmentCount}
                        </span>}
                    </button>;
            })}
              </div>
            </CardContent>
          </Card>

          {/* Selected Date Appointments */}
          <Card data-id="element-191">
            <CardHeader data-id="element-192">
              <CardTitle data-id="element-193">
                {selectedDate === today ? 'Citas de Hoy' : `Citas del ${selectedDate}`}
              </CardTitle>
            </CardHeader>
            <CardContent data-id="element-194">
              {(() => {
            // Filter selected date appointments by ownership
            const visibleDateAppointments = selectedDateAppointments.filter(apt => {
              if (isOwnOnly && linkedDoctor) {
                return apt.doctorId === linkedDoctor.id;
              }
              return true;
            });
            return visibleDateAppointments.length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-195">
                    No hay citas programadas
                  </p> : <div className="space-y-3" data-id="element-196">
                    {visibleDateAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-197">
                          <div className="flex items-start justify-between mb-2" data-id="element-198">
                            <div data-id="element-199">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-200">
                                {formatTime(apt.time)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-201">
                                {apt.duration} min
                              </p>
                            </div>
                            <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-202">
                              {getStatusName(apt.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-900 dark:text-gray-100" data-id="element-203">
                            {patient?.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-204">
                            Dr. {doctor?.name} • {service?.name}
                          </p>
                        </div>;
              })}
                  </div>;
          })()}
            </CardContent>
          </Card>
        </div>}

      {/* New Appointment Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nueva Cita" size="lg" icon={<CalendarIcon className="w-6 h-6" data-id="element-206" />} accentColor="sky" data-id="element-205">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-207">
          <FormSection title="Paciente" data-id="element-208">
            <Select label="Seleccionar Paciente" options={patientOptions} value={formData.patientId} onChange={value => setFormData({
            ...formData,
            patientId: value
          })} error={formErrors.patientId} searchable placeholder="Buscar paciente..." data-id="element-209" />
          </FormSection>

          <FormSection title="Doctor" data-id="element-210">
            {/* If "own" permission, show locked doctor info instead of selectors */}
            {isOwnOnly && linkedDoctor ? <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-4" data-id="element-211">
                <p className="text-sm text-sky-700 dark:text-sky-400 font-medium" data-id="element-212">
                  Doctor asignado automáticamente
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1" data-id="element-213">
                  Dr. {linkedDoctor.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-214">
                  {getSpecialty(linkedDoctor.specialtyId)?.name || ''}
                </p>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-215">
                <Select label="Especialidad (opcional)" options={specialtyOptions} value={selectedSpecialty} onChange={value => {
              setSelectedSpecialty(value);
              setFormData({
                ...formData,
                doctorId: ''
              });
            }} data-id="element-216" />
                <Select label="Doctor" options={doctorOptions} value={formData.doctorId} onChange={value => setFormData({
              ...formData,
              doctorId: value
            })} error={formErrors.doctorId} searchable placeholder="Seleccionar doctor..." data-id="element-217" />
              </div>}
          </FormSection>

          <FormSection title="Servicio" data-id="element-218">
            <Select label="Seleccionar Servicio" options={serviceOptions} value={formData.serviceId} onChange={value => setFormData({
            ...formData,
            serviceId: value
          })} error={formErrors.serviceId} placeholder="Seleccionar servicio..." data-id="element-219" />
          </FormSection>

          <FormSection title="Fecha y Hora" data-id="element-220">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-221">
              <Input type="date" label="Fecha" value={formData.date} onChange={e => setFormData({
              ...formData,
              date: e.target.value
            })} error={formErrors.date} min={today} data-id="element-222" />
              <Input type="time" label="Hora" value={formData.time} onChange={e => setFormData({
              ...formData,
              time: e.target.value
            })} error={formErrors.time} data-id="element-223" />
            </div>
          </FormSection>

          <FormSection title="Notas" data-id="element-224">
            <Textarea label="Notas Adicionales" value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} rows={3} placeholder="Detalles adicionales sobre la cita..." data-id="element-225" />
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-226">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-227">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-228">Crear Cita</Button>
          </div>
        </form>
      </Modal>

      {/* Cancel Modal */}
      <Modal isOpen={isCancelOpen} onClose={() => setIsCancelOpen(false)} title="Cancelar Cita" size="md" data-id="element-229">
        <div className="space-y-4" data-id="element-230">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-231">
            ¿Está seguro de que desea cancelar esta cita? Se notificará al
            paciente y al doctor.
          </p>
          <Textarea label="Motivo de cancelación" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Ingrese el motivo de la cancelación..." rows={3} data-id="element-232" />
          <div className="flex justify-end gap-3" data-id="element-233">
            <Button variant="outline" onClick={() => setIsCancelOpen(false)} data-id="element-234">
              Volver
            </Button>
            <Button variant="danger" onClick={handleCancel} data-id="element-235">
              Cancelar Cita
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reschedule Modal */}
      <Modal isOpen={isRescheduleOpen} onClose={() => setIsRescheduleOpen(false)} title="Reprogramar Cita" size="md" data-id="element-236">
        <div className="space-y-4" data-id="element-237">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-238">
            Seleccione la nueva fecha y hora para la cita.
          </p>
          <div className="grid grid-cols-2 gap-4" data-id="element-239">
            <Input type="date" label="Nueva Fecha" value={rescheduleData.date} onChange={e => setRescheduleData({
            ...rescheduleData,
            date: e.target.value
          })} min={today} data-id="element-240" />
            <Input type="time" label="Nueva Hora" value={rescheduleData.time} onChange={e => setRescheduleData({
            ...rescheduleData,
            time: e.target.value
          })} data-id="element-241" />
          </div>
          <div className="flex justify-end gap-3" data-id="element-242">
            <Button variant="outline" onClick={() => setIsRescheduleOpen(false)} data-id="element-243">
              Cancelar
            </Button>
            <Button onClick={handleReschedule} data-id="element-244">Reprogramar</Button>
          </div>
        </div>
      </Modal>

      {/* Complete Appointment Modal */}
      <Modal isOpen={isCompleteOpen} onClose={() => setIsCompleteOpen(false)} title="Completar Cita" size="md" data-id="element-245">
        <div className="space-y-4" data-id="element-246">
          {selectedAppointment && <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-247">
              <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-248">
                <div data-id="element-249">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-250">
                    Paciente:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-251">
                    {getPatient(selectedAppointment.patientId)?.name}
                  </p>
                </div>
                <div data-id="element-252">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-253">
                    Doctor:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-254">
                    Dr. {getDoctor(selectedAppointment.doctorId)?.name}
                  </p>
                </div>
                <div data-id="element-255">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-256">
                    Fecha:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-257">
                    {selectedAppointment.date} -{' '}
                    {formatTime(selectedAppointment.time)}
                  </p>
                </div>
                <div data-id="element-258">
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-259">
                    Servicio:
                  </span>
                  <p className="font-medium text-gray-900 dark:text-white" data-id="element-260">
                    {getService(selectedAppointment.serviceId)?.name}
                  </p>
                </div>
              </div>
            </div>}

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4" data-id="element-261">
            <p className="text-sm text-green-800 dark:text-green-300" data-id="element-262">
              ¿Confirmar que esta cita fue completada exitosamente?
            </p>
          </div>

          <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-263">
            <div className="flex items-center gap-2 mb-3" data-id="element-264">
              <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-265" />
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-266">
                Próxima Cita Sugerida
              </h4>
              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full" data-id="element-267">
                Opcional
              </span>
            </div>
            <div className="space-y-3" data-id="element-268">
              <Input type="date" label="Fecha Sugerida" value={completeData.nextAppointmentDate} onChange={e => setCompleteData({
              ...completeData,
              nextAppointmentDate: e.target.value
            })} min={getToday()} data-id="element-269" />
              <Textarea label="Notas para la Próxima Cita" value={completeData.nextAppointmentNotes} onChange={e => setCompleteData({
              ...completeData,
              nextAppointmentNotes: e.target.value
            })} placeholder="Ej: Control post-tratamiento, siguiente sesión..." rows={2} data-id="element-270" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2" data-id="element-271">
            <Button variant="outline" onClick={() => setIsCompleteOpen(false)} data-id="element-272">
              Cancelar
            </Button>
            <Button onClick={handleComplete} leftIcon={<CheckIcon className="w-4 h-4" data-id="element-274" />} data-id="element-273">
              Completar Cita
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}